import { FieldTimeOutlined, ToolOutlined } from '@ant-design/icons';
import { Button, Card, Col, InputNumber, Row, Select, Table, Tooltip } from 'antd';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { updateAdditionalWorkPlaceItem } from '../../../../../../contextRedux/AdditionalAct/AdditionalActModel';
import { RootState } from '../../../../../../store';
import { Column } from '../../../../../../utils/column';
import './DevicesTable.css';

const { Option } = Select;

const DevicesTable: React.FC<any> = ({}) => {
  const isMobile = useMediaQuery({ query: '(max-width: 480px)' });
  const dispatch = useDispatch();
  const addedDevices = useSelector((state: RootState) => state?.models?.additionalAct.contractWorkPlaceItems);
  const additionalAct = useSelector((state: RootState) => state?.models?.additionalAct);

  const handleDeviceChange = (record: any, value: any, dataIndex: string) => {
    const updatedRecord = { ...record, [dataIndex]: value };

    dispatch(
      updateAdditionalWorkPlaceItem({
        index: addedDevices.findIndex(
          (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
        ),
        updates: updatedRecord,
      })
    );
  };

  const handleUnitChange = (record: any, newUnit: string) => {
    // Find the index of the record in addedDevices
    const recordIndex = addedDevices.findIndex(
      (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
    );

    // If the record is found, proceed with the update
    if (recordIndex !== -1) {
      const updatedRecord = { ...addedDevices[recordIndex] };

      if (newUnit === 'hour') {
        updatedRecord.laborTime = Number.parseFloat((60 * updatedRecord.piece)?.toFixed(2));
        updatedRecord.laborPrice = Number.parseFloat(
          (additionalAct.laborTab.laborPriceOnHour ?? 300 * updatedRecord.piece)?.toFixed(2)
        );
        updatedRecord.laborPriceOnHour = Number.parseFloat((additionalAct.laborTab.laborPriceOnHour ?? 300).toFixed(2));
      } else {
        const device = updatedRecord.device ? { ...updatedRecord.device } : {};

        updatedRecord.laborTime = Number.parseFloat(
          ((device.laborTimeSM + (device.totalLaborsTimeSM ?? 0)) * updatedRecord.piece)?.toFixed(2)
        );
        updatedRecord.laborPrice = Number.parseFloat(
          ((device.laborPriceSM + (device.totalLaborsPriceSM ?? 0)) * updatedRecord.piece)?.toFixed(2)
        );
      }

      // Update the unit measure
      updatedRecord.unitMeasure = newUnit;

      // Dispatch the update for the specific record in the Redux state
      dispatch(
        updateAdditionalWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === record.deviceTypeId && item.workPlaceId === record.workPlaceId
          ),
          updates: updatedRecord,
        })
      );
    }
  };

  const renderColumn = (column: any, _text: string, record: any) => {
    const value = Array.isArray(column.dataIndex)
      ? column.dataIndex.reduce((acc: any, key: any) => acc[key], record)
      : record[column.dataIndex];

    if (column.editable) {
      return (
        <div className='diableButton-input-container'>
          {column.dataIndex === 'laborTime' && (
            <Button disabled className='disableButton'>
              <b>
                <FieldTimeOutlined style={{ marginRight: 5 }} />
                {record.unitMeasure === 'buc' ? record.device.laborTimeSM + ' min/buc' : '60 min'}
              </b>
            </Button>
          )}
          {column.dataIndex === 'laborPrice' && (
            <Button disabled className='disableButton'>
              <b>
                <ToolOutlined style={{ marginRight: 5 }} />
                {record.unitMeasure === 'buc'
                  ? record.device.laborPriceSM + ' lei/buc'
                  : Number.parseFloat((record.laborPrice / record.piece).toFixed(2)) + ' lei/ora'}
              </b>
            </Button>
          )}
          <InputNumber
            min={1}
            value={value}
            disabled={column.dataIndex === 'piece'}
            onChange={(value) => handleDeviceChange(record, value, column.dataIndex)}
            className='disableButton'
          />
          {column.dataIndex === 'piece' && (
            <Select
              value={record?.unitMeasure || 'buc'}
              onChange={(newUnit: string) => handleUnitChange(record, newUnit)}
              className='input-width'
            >
              <Option value='buc'>buc</Option>
              <Option value='hour'>hour</Option>
            </Select>
          )}
        </div>
      );
    }
    return value;
  };

  const calculateWeightedAverage = (unitMeasure: string) => {
    let sumWeightedCosts = 0;
    let laborTimePerPieces = 0;

    addedDevices
      ?.filter((ad: any) => ad.unitMeasure === unitMeasure)
      .forEach((item: any) => {
        const { laborTime, laborPrice, piece } = item;
        sumWeightedCosts += Number.parseFloat((piece * (((laborPrice / piece) * 60) / (laborTime / piece))).toFixed(2));
        laborTimePerPieces += piece;
      });

    return Number.parseFloat((sumWeightedCosts / laborTimePerPieces).toFixed(2));
  };

  const columnsFirstTable: Column[] = [
    {
      title: 'No.',
      key: 'index',
      dataIndex: 'index',
      width: 100,
      render: (_text: any, _record: any, index: any) => <div>{index + 1 + '.'}</div>,
    },
    {
      title: 'Device',
      key: 'device',
      width: 200,
      render: (record) => (
        <div style={{ display: 'grid' }}>
          <Tooltip title={record.device?.name}>
            <div className='ellipsis-cell'>
              <span>{record.device?.name}</span>
            </div>
          </Tooltip>
          {record?.device?.imageLink[0] !== '' && record?.device?.imageLink[0].includes('.jpg', '.png') ? (
            <img src={record?.device?.imageLink[0]} alt='' style={{ width: '100px', height: 'auto' }} />
          ) : (
            ''
          )}
        </div>
      ),
      dataIndex: '',
    },
    {
      title: 'Locatie',
      dataIndex: 'workPlace',
      key: 'workPlace',
      render: (workPlace) => (
        <Tooltip title={workPlace?.name}>
          <div className='ellipsis-cell'>{workPlace?.name}</div>
        </Tooltip>
      ),
    },
    {
      title: 'Cantitate',
      dataIndex: 'piece',
      key: 'piece',
      editable: true,
    },
    {
      title: 'Unitate De Masura',
      dataIndex: 'unitMeasure',
      key: 'unitMeasure',
      hidden: true,
    },
    {
      title: 'Cost Manopera Per Bucata (lei)',
      dataIndex: 'laborPrice',
      key: 'laborPrice',
      editable: true,
    },
    {
      title: 'Timp Manopera Estimat (minute)',
      dataIndex: 'laborTime',
      key: 'laborTime',
      editable: true,
    },
    {
      title: 'Periodicitate',
      dataIndex: 'period',
      key: 'period',
      render: (period) => <div>{period}</div>,
    },
    {
      title: 'Pret Total/Periodicitate (lei)',
      key: 'totalPrice',
      width: 200,
      dataIndex: '',
      render: (record) => {
        const pricePerPeriod =
          record.laborPrice *
          record.piece *
          (record.period === 'lunar'
            ? 12
            : record.period === 'trimestrial'
            ? 4
            : record.period === 'semestrial'
            ? 2
            : record.period === 'anual'
            ? 1
            : 1);

        return <div>{Number.parseFloat(pricePerPeriod.toFixed(2))} lei</div>;
      },
    },
  ];

  const calculateTotalPricePerPeriod = (record: any) =>
    record.laborPrice *
    record.piece *
    (record.period === 'lunar'
      ? 12
      : record.period === 'trimestrial'
      ? 4
      : record.period === 'semestrial'
      ? 2
      : record.period === 'anual'
      ? 1
      : 1);

  //Mobile view render card
  const renderCard = (record: any) => (
    <Card key={record.id} className='deviceCard'>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24}>
          {record?.device?.imageLink[0]?.includes('.jpg', '.png') && (
            <img src={record.device.imageLink[0]} alt='device' className='image' />
          )}
        </Col>
        <Col xs={24} sm={12}>
          <Tooltip title={record.device?.name}>
            <div className='ellipsisCell'>
              <strong>Device: </strong> {record.device?.name}
            </div>
          </Tooltip>
        </Col>
        <Col xs={24} sm={12}>
          <strong>Locatie: </strong>
          {record.workPlace?.name}
        </Col>
        <Col xs={24} sm={12}>
          <strong>Periodicitate: </strong>
          {record.period}
        </Col>
        <Col xs={12} sm={12}>
          <strong>Cantitate: </strong>
          <br />
          <Button disabled className='disableButton'>
            <b>{record.piece}</b>
          </Button>
          <Select
            value={record?.unitMeasure || 'buc'}
            onChange={(newUnit: string) => handleUnitChange(record, newUnit)}
            className='input-width'
          >
            <Option value='buc'>buc</Option>
            <Option value='hour'>hour</Option>
          </Select>
        </Col>
        <Col xs={12} sm={12}>
          <strong>Cost Manopera: </strong>
          <br />
          <Button disabled className='disableButton'>
            <b>
              <ToolOutlined style={{ marginRight: 5 }} />
              {record.unitMeasure === 'buc'
                ? record.device.laborPriceSM + ' lei/buc'
                : Number.parseFloat((record.laborPrice / record.piece).toFixed(2)) + ' lei/ora'}
            </b>
          </Button>
          <InputNumber
            min={1}
            value={record.laborPrice}
            onChange={(value) => handleDeviceChange(record, value, 'laborPrice')}
            className='input-width'
          />
        </Col>
        <Col xs={12} sm={12}>
          <strong>Timp Manopera:</strong> <br />
          <Button disabled className='disableButton'>
            {' '}
            <b>
              <FieldTimeOutlined style={{ marginRight: 5 }} />
              {record.unitMeasure === 'buc' ? record.device.laborTimeSM + ' min/buc' : '60 min'}
            </b>
          </Button>
          <InputNumber
            min={1}
            value={record.laborTime}
            onChange={(value) => handleDeviceChange(record, value, 'laborTime')}
            className='input-width'
          />
        </Col>
        <Col xs={24} sm={24}>
          <div className='totalPriceName'>
            <strong>Pret Total/Periodicitate: </strong> <br />
          </div>
          <div className='totalPrice'>{calculateTotalPricePerPeriod(record)} lei</div>
        </Col>
      </Row>
    </Card>
  );

  //Mobile view summary row
  const renderSummary = () => {
    const totalPretTotalPeriodicitate = addedDevices?.reduce(
      (total, item) => total + calculateTotalPricePerPeriod(item),
      0
    );

    return (
      <Row>
        <Col xs={24} sm={24}>
          {addedDevices.filter((ad) => ad.unitMeasure === 'buc').length > 0 && (
            <div className='totalCard'>
              <strong className='totalLab'>Manopera bucata/ora</strong>
              <div className='totalLab2'>{calculateWeightedAverage('buc')} lei</div>
            </div>
          )}
        </Col>

        <Col xs={24} sm={24}>
          {addedDevices.filter((ad) => ad.unitMeasure === 'hour').length > 0 && (
            <div className='totalCard'>
              <div style={{ flex: 1 }}>
                <strong className='totalLab'>Manopera/ora</strong>
              </div>
              <div className='totalLab2'>{calculateWeightedAverage('hour')} lei</div>
            </div>
          )}
        </Col>

        <Col xs={24} sm={24}>
          <div className='total'>
            <div style={{ flex: 1 }}>
              <strong className='totalLab'>Total</strong>
            </div>
            <div style={{ flex: 1 }}>
              <strong className='totalLab2'>
                {addedDevices.reduce((total, item) => total + item.laborTime, 0)} minute
              </strong>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <strong className='totalLab2'>{totalPretTotalPeriodicitate.toFixed(2)} lei</strong>
            </div>
          </div>
        </Col>
      </Row>
    );
  };

  //Large screnn  table view
  const renderTable = () => (
    <Table
      rowClassName='table-row-dark'
      dataSource={addedDevices}
      rowKey={(addedDevice) => addedDevice.id}
      columns={columnsFirstTable.map((column, index) => ({
        ...column,
        key: index,
        render: column.render || ((text, record) => renderColumn(column, text, record)),
      }))}
      pagination={false}
      scroll={{ x: 'max-content', y: 70 * 10 }}
      style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), 0 -4px 6px rgba(0, 0, 0, 0.1)' }}
      summary={() => {
        const totalPretTotalPeriodicitate = addedDevices?.reduce(
          (total, item) => total + calculateTotalPricePerPeriod(item),
          0
        );

        return (
          <>
            {/* Existing summary rows */}
            {addedDevices.filter((ad) => ad.unitMeasure === 'buc').length > 0 && (
              <Table.Summary.Row style={{ backgroundColor: '#e2e3e5' }}>
                <Table.Summary.Cell index={0}>
                  <strong>Manopera bucata/ora </strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                <Table.Summary.Cell index={2}></Table.Summary.Cell>
                <Table.Summary.Cell index={3}></Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <strong>{calculateWeightedAverage('buc')} lei</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5}></Table.Summary.Cell>
                <Table.Summary.Cell index={6}></Table.Summary.Cell>
                <Table.Summary.Cell index={7}></Table.Summary.Cell>
              </Table.Summary.Row>
            )}

            {addedDevices.filter((ad) => ad.unitMeasure === 'hour').length > 0 && (
              <Table.Summary.Row style={{ backgroundColor: '#e2e3e5' }}>
                <Table.Summary.Cell index={0}>
                  <strong>Manopera/ora</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                <Table.Summary.Cell index={2}></Table.Summary.Cell>
                <Table.Summary.Cell index={3}></Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <strong>{calculateWeightedAverage('hour')} lei</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5}></Table.Summary.Cell>
                <Table.Summary.Cell index={6}></Table.Summary.Cell>
                <Table.Summary.Cell index={7}></Table.Summary.Cell>
              </Table.Summary.Row>
            )}

            {/* Total Summary Row */}
            <Table.Summary.Row style={{ backgroundColor: '#e2e3e5' }}>
              <Table.Summary.Cell index={0}>
                <strong>Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}></Table.Summary.Cell>
              <Table.Summary.Cell index={2}></Table.Summary.Cell>
              <Table.Summary.Cell index={3}></Table.Summary.Cell>
              <Table.Summary.Cell index={4}></Table.Summary.Cell>
              <Table.Summary.Cell index={5}>
                <strong>{addedDevices.reduce((total, item) => total + item.laborTime, 0)} minute</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}></Table.Summary.Cell>
              <Table.Summary.Cell index={7}>
                <strong>{totalPretTotalPeriodicitate.toFixed(2)} lei</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </>
        );
      }}
    />
  );
  return (
    <div>
      {isMobile ? (
        <div>
          {addedDevices.map(renderCard)}
          {addedDevices.some((ad) => ad.unitMeasure === 'buc') && renderSummary()}
        </div>
      ) : (
        renderTable()
      )}
    </div>
  );
};

export default DevicesTable;
